import { z } from 'zod';
import { ClassificationActionsSchema, ClassificationSignalsSchema, SenderContextSchema, TrackSchema } from './classify';

/**
 * Dev-only "copy email details" payload (see the copy button in the Cleanup
 * email-detail panel).
 *
 * This is the complete set of runtime inputs a gold-set fixture needs, so a
 * pasted payload can be turned into a fixture WITHOUT a Supabase round trip:
 *   .github/skills/purebox-add-gold-example-fixture-email/SKILL.md
 *
 * The suggestion-detail response (`ClassificationSuggestionSchema`) deliberately
 * omits `thread_id`, raw headers, unfiltered Gmail labels, `llm_metadata`, and
 * the full `sender_insights` row - none of them drive the UI. They ARE required
 * to replay a classification, which is why they live here on a separate,
 * dev-gated endpoint instead of bloating every list/detail payload in prod.
 */

const EmailHeaderValueSchema = z.union([z.string(), z.array(z.string())]);

/** Normalized fixture inputs plus the complete source `emails` row. */
export const SuggestionDebugEmailSchema = z.object({
  message_id: z.string(),
  thread_id: z.string().nullable(),
  from_address: z.string().nullable(),
  subject: z.string().nullable(),
  snippet: z.string().nullable(),
  received_at: z.string().nullable(),
  size_bytes: z.number().nullable(),
  category: z.string().nullable(),
  /** UNFILTERED Gmail label ids - not the whitelisted `gmail_labels` the UI shows. */
  labels: z.array(z.string()),
  /** All stored headers with original casing and repeated values intact. */
  headers: z.record(z.string(), EmailHeaderValueSchema),
  event_start_utc: z.string().nullable(),
  event_end_utc: z.string().nullable(),
  is_recurring: z.boolean().nullable(),
  /** Complete JSON-safe `emails` row. Retained for provenance; classifiers ignore it. */
  raw_metadata: z.record(z.string(), z.unknown()),
});

export type SuggestionDebugEmail = z.infer<typeof SuggestionDebugEmailSchema>;

/**
 * Raw `sender_insights` row. Superset of `SenderContextSchema` - carries the
 * typed preference evidence (#690) and header-derived marketing ratio (#363)
 * that the UI never renders but the classifier reads.
 *
 * Rates are `null` (never 0.0) below the support floor. Never substitute 0.
 */
export const SuggestionDebugSenderInsightsSchema = z.object({
  sender_email: z.string(),
  sender_domain: z.string().nullable(),
  total_emails_received: z.number().nullable(),
  open_rate: z.number().nullable(),
  reply_rate: z.number().nullable(),
  archive_rate: z.number().nullable(),
  trash_rate: z.number().nullable(),
  archive_without_read_rate: z.number().nullable(),
  trash_without_read_rate: z.number().nullable(),
  kept_after_read_rate: z.number().nullable(),
  star_rate: z.number().nullable(),
  mark_important_rate: z.number().nullable(),
  custom_label_rate: z.number().nullable(),
  owner_trash_override_rate: z.number().nullable(),
  preference_evidence_support: z.number().nullable(),
  has_unsubscribe_link_rate: z.number().nullable(),
  marketing_ratio_headers: z.number().nullable(),
  marketing_headers_count: z.number().nullable(),
  is_in_contacts: z.boolean().nullable(),
  has_prior_reply_thread: z.boolean().nullable(),
  same_org_domain: z.boolean().nullable(),
  last_recalculated_at: z.string().nullable(),
});

export type SuggestionDebugSenderInsights = z.infer<typeof SuggestionDebugSenderInsightsSchema>;

export const SuggestionDebugPayloadSchema = z.object({
  user_email: z.string().nullable(),
  env: z.enum(['dev', 'prod']),
  suggestion_id: z.string(),
  message_id: z.string(),
  email_subject: z.string().nullable(),
  sender_email: z.string().nullable(),
  snippet: z.string().nullable(),
  received_at: z.string().nullable(),
  created_at: z.string().nullable(),
  track: TrackSchema,
  labels: z.array(z.string()),
  actions: ClassificationActionsSchema,
  signals: ClassificationSignalsSchema.optional(),
  confidence: z.number(),
  display_confidence: z.number().optional(),
  rationale: z.string().nullable(),
  phase1_match: z.boolean(),
  llm_used: z.boolean(),
  status: z.string(),
  /** Whitelisted Gmail labels, exactly as the detail panel receives them. */
  gmail_labels: z.array(z.string()),
  sender_context: SenderContextSchema.optional(),
  label_freetext: z.string().nullable().optional(),
  label_canonical: z.string().nullable().optional(),
  risk_type: z.string().nullable().optional(),
  event_state: z.string().nullable().optional(),
  action_required: z.boolean().nullable().optional(),
  /** Phase 2 rule-matching metadata written by the classifier. Shape is open. */
  llm_metadata: z.record(z.string(), z.unknown()).nullable(),
  matched_rule_id: z.string().nullable(),
  decision_source: z.string().nullable(),
  decision_path: z.string().nullable(),
  applied_policy_id: z.string().nullable(),
  safety_reason_code: z.string().nullable(),
  decision_provenance_version: z.number().nullable(),
  scan_job_id: z.string().nullable(),
  user_verdict: z.string().nullable(),
  user_action: z.string().nullable(),
  /** Null when no `emails` row survives retention - headers must then be hand-authored. */
  email: SuggestionDebugEmailSchema.nullable(),
  /** Null when the sender has no insights row yet. Do NOT fabricate zeros. */
  sender_insights: SuggestionDebugSenderInsightsSchema.nullable(),
});

export type SuggestionDebugPayload = z.infer<typeof SuggestionDebugPayloadSchema>;
